/**
 * @fileoverview Composable for managing navigation away prompts
 * @module composables/useNavigationPrompt
 *
 * Determines when to prompt users before they navigate away from the page,
 * based on widget state and livechat session status.
 */
import { computed, type Ref, type ComputedRef } from 'vue'

/**
 * Composable for managing navigation away prompts
 *
 * @param chatWindow - Reactive computed ref indicating if chat window is open
 * @param settings - Reactive computed ref containing prompt settings
 * @returns Computed property indicating if prompt should be shown
 */
export function useNavigationPrompt(
  chatWindow: ComputedRef<boolean>,
  settings: ComputedRef<any>
) {
  /**
   * Determine if user should be prompted before navigating away
   *
   * Conditions:
   * - "widget_is_opened": Prompt when chat window is open
   * - "during_livechat_session": Prompt during active livechat session
   * - false/undefined: Never prompt
   */
  const shouldPromptBeforeNavigateAway = computed(() => {
    const promptSetting = settings.value.promptBeforeNavigateAway

    if (!promptSetting) {
      return false
    }

    const chatWindowIsOpened = chatWindow.value
    const isInLivechatSession = settings.value.liveChatStatus
    const shouldPromptWhenWidgetOpen = promptSetting === 'widget_is_opened'
    const shouldPromptDuringLivechat = promptSetting === 'during_livechat_session'

    return (
      (shouldPromptWhenWidgetOpen && chatWindowIsOpened) ||
      (shouldPromptDuringLivechat && isInLivechatSession)
    )
  })

  return {
    shouldPromptBeforeNavigateAway,
  }
}
